import { IEditCommon, TUpdateChannelToBrandRequest } from 'src/api/updateChannel/types';
import { IFormData, TEditFormDataKeys, TSecretsFieldsKeys, TSnakeCaseKeys } from '../types';
import { toSnakeCase } from 'src/utils/toSnakeCase/toSnakeCase';
import { IConfiguredAddons } from 'src/api/getBrands/types';

const getDirtyKeys = (
  dirtyFields: IFormData,
  currentChannel: IConfiguredAddons,
  preferredName: string,
): { keys: string[]; secretKeys: string[] } => {
  const commonKeys = Object.keys(dirtyFields).filter(
    (key) => key !== 'secrets' && key !== 'endpoints' && key !== 'config',
  );
  const secretKeys = Object.keys((dirtyFields?.secrets as unknown as TSecretsFieldsKeys) ?? {});
  const keys = commonKeys.concat(secretKeys);

  if (keys.includes('preferredName') && currentChannel.preferred_name === preferredName) {
    keys.splice(keys.indexOf('preferredName'), 1);
  }

  return { keys, secretKeys };
};

const buildFieldsPayload = (
  data: IFormData,
  currentChannel: IConfiguredAddons,
  keys: string[],
  secretKeys: string[],
): IEditCommon => {
  if (keys.length === 0) {
    return {};
  }

  const isSecretValueEmpty = secretKeys.some(
    (key) => data.secrets[key as TSecretsFieldsKeys] === '',
  );
  let payload: IEditCommon = {};

  if (secretKeys.length > 0 || keys.includes('username')) {
    payload = { ...payload, authentication_type: currentChannel.authentication_type };
    if (secretKeys.includes('password')) {
      payload = { ...payload, username: data.username };
    }
  }

  keys.forEach((key) => {
    const targetKey = secretKeys.includes(key) && !isSecretValueEmpty ? data.secrets : data;
    payload = {
      ...payload,
      [toSnakeCase(key) as TSnakeCaseKeys]: targetKey[key as TEditFormDataKeys],
    };
  });

  return payload;
};

const buildEndpointsPayload = (
  data: IFormData,
  dirtyFields: IFormData,
): { endpoints: Array<{ endpoint_id: string; url: string | null }> } | object => {
  if (!dirtyFields.endpoints || !data.endpoints?.length) {
    return {};
  }

  const endpointUpdates = data.endpoints
    .filter((_, index) => dirtyFields.endpoints?.[index])
    .map((ep) => ({
      endpoint_id: ep.endpoint_id,
      url: ep.url.trim() === '' ? null : ep.url.trim(),
    }));

  return endpointUpdates.length > 0 ? { endpoints: endpointUpdates } : {};
};

const buildConfigPayload = (
  data: IFormData,
  dirtyFields: IFormData,
): { config: Record<string, string | number | boolean> } | object => {
  if (!dirtyFields.config || !data.config) {
    return {};
  }

  const dirtyConfigFields = dirtyFields.config as unknown as Record<string, boolean>;

  if (typeof dirtyConfigFields !== 'object' || dirtyConfigFields === null) {
    return { config: data.config };
  }

  const dirtyConfig: Record<string, string | number | boolean> = {};
  Object.keys(dirtyConfigFields).forEach((key) => {
    if (dirtyConfigFields[key] && data.config[key] !== undefined) {
      dirtyConfig[key] = data.config[key];
    }
  });

  return Object.keys(dirtyConfig).length > 0 ? { config: dirtyConfig } : {};
};

export const buildEditPayload = (
  data: IFormData,
  currentChannel: IConfiguredAddons,
  dirtyFields: IFormData,
): TUpdateChannelToBrandRequest => {
  const { keys, secretKeys } = getDirtyKeys(dirtyFields, currentChannel, data.preferredName);

  return {
    ...buildFieldsPayload(data, currentChannel, keys, secretKeys),
    ...buildEndpointsPayload(data, dirtyFields),
    ...buildConfigPayload(data, dirtyFields),
  };
};
